/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { colors, mixins, LoadingSpinner } from '@blend/consumer-components';
import { useLocation } from 'react-router-dom';

import { setCurrentDocument } from '../../../../actions/documentActions';
import ClosingDocument from '../../../common/Documents/ClosingDocument';
import { useRootSelector } from 'blb-hooks/useRootSelector';
import { Document } from './Document';
import { size } from 'lodash';

export namespace Documents {
  export interface Props {
    documents: ClosingDocument[] | null;
    onSelectDocument: (doc: ClosingDocument) => void;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
    ':last-child': {
      borderBottom: 'none',
    },
  },
  headingWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: colors.catalystLight01,
    ...mixins.padding(8),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
    ':first-child': {
      ...mixins.borderTop(1, 'solid', mixins.globalBorderColor),
    },
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIcon: {
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    ...mixins.margin(0, 10, 0, 0),
    ...mixins.padding(0),
  },
});

export const Documents: React.FC<Documents.Props> = ({ documents, onSelectDocument }) => {
  const dispatch = useDispatch();
  const { currentDoc: currentDocState } = useRootSelector(state => state.document);
  const [currentDocId, setCurrentDocId] = useState<string | null>(null);
  const location = useLocation();

  const isViewingENotes = location.pathname.includes('/enote');
  const documentTypeText = isViewingENotes ? 'eNote Documents' : 'eSign Documents';

  const showLoading = !documents || documents.length === 0;

  useEffect(() => {
    const foundDoc = documents?.find(document => document.id === currentDocId);
    const shouldUpdateCurrentDoc = foundDoc !== undefined;
    if (shouldUpdateCurrentDoc) {
      setCurrentDocument(foundDoc)(dispatch);
    }
  }, [dispatch, documents, currentDocId]);

  const handleDocumentClick = (document: ClosingDocument) => {
    setCurrentDocId(document.id);
    setCurrentDocument(document)(dispatch);
    onSelectDocument(document);
  };

  return (
    <div className={css(styles.wrapper)} data-it="Documents">
      {showLoading ? (
        <div className={css(styles.loadingWrapper)}>
          <LoadingSpinner color={colors.catalyst} size={'sm'} />
        </div>
      ) : (
        <>
          <div className={css(styles.headingWrapper)} data-it="document-package">
            <div>
              {documentTypeText} (<span data-it="document-package-count">{size(documents)}</span>)
            </div>
          </div>
          {documents.map(d => (
            <Document
              document={d}
              handleClick={handleDocumentClick}
              isSelected={!!currentDocState && currentDocState.id === d.id}
              key={d.id}
            />
          ))}
        </>
      )}
    </div>
  );
};
